import { Component, Input, OnDestroy, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, AbstractControl, NG_VALIDATORS, Validator } from '@angular/forms';
import * as moment from 'moment';

@Component({
  selector: 'app-month-picker',
  templateUrl: './month-picker.component.html',
  styleUrls: ['./month-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthPickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MonthPickerComponent),
      multi: true
    }
  ]
})
export class MonthPickerComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  form: FormGroup;
  currentYear = moment().year();
  internalInputHeight = '30px';
  internalInputWith = '100%';
  internalMinYear = (moment().year() - 5);
  internalMaxYear = this.currentYear;
  togglePicker = false;
  yearSelected = null;
  monthSelected = null;
  statusBtnNext = false;
  statusBtnPrev = false;
  isValidToogle = false;
  statusControl = null;

  @Input() set inputWith(value: any) {
    this.internalInputWith = value;
  }

  @Input() set minYear(value: number) {
    this.internalMinYear = value;
  }

  @Input() set maxYear(value: number) {
    this.internalMaxYear = value;
  }

  @Input() set inputHeight(value: any) {
    this.internalInputHeight = value;
  }

  listMonths = [
    { month: 'Jan', numberMonth: 1 },
    { month: 'Feb', numberMonth: 2 },
    { month: 'Mar', numberMonth: 3 },
    { month: 'Apr', numberMonth: 4 },
    { month: 'May', numberMonth: 5 },
    { month: 'Jun', numberMonth: 6 },
    { month: 'Jul', numberMonth: 7 },
    { month: 'Aug', numberMonth: 8 },
    { month: 'Sep', numberMonth: 9 },
    { month: 'Oct', numberMonth: 10 },
    { month: 'Nov', numberMonth: 11 },
    { month: 'Dec', numberMonth: 12 }
  ];

  get monthAndYearValueCtrl(): AbstractControl {
    return this.form.get('monthAndYearValue');
  }

  get yearValueCtrl(): AbstractControl {
    return this.form.get('yearValue');
  }

  onChange: any = (value: any) => {};
  onTouch: any = () => {};
  onValidationChangeFn: any = () => {};

  constructor(private fb: FormBuilder) {
    this.getYears();
  }

  ngOnInit(): void {
    this.form = this.fbGroup();
    this.onValidationChangeFn();
  }

  private fbGroup(): FormGroup {
    return this.fb.group({
      monthAndYearValue: null,
      yearValue: null
    });
  }

  writeValue(value: any): void {
    if (value !== null && value !== undefined) {
      const formatDate = moment(value, 'YYYY-MM-DD');

      if (formatDate.isValid()) {
        const nameMonth = formatDate.format('MMM');
        const numberMonth = formatDate.format('MM');
        const year  = formatDate.year();
        const textValue = `${nameMonth}, ${year}`;
        this.monthAndYearValueCtrl.patchValue(textValue);
        this.yearSelected = year;
        this.monthSelected = numberMonth;
      } else {
        this.monthAndYearValueCtrl.patchValue(null);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChangeFn = fn;
  }

  validate(control: AbstractControl): { [key: string]: any; } {
    let objValid = null;

    if (control.errors && (control.value === undefined || control.value === null)) {
      objValid = { invalid: true };
    }
    this.statusControl = objValid;

    return objValid;
  }

  getYears(): number[] {
    return new Array((this.internalMaxYear - this.internalMinYear + 1))
    .fill(undefined).map((_, count) => (count + this.internalMinYear));
  }

  showMonthPicker(event): void {
    if (event && event.type === 'click') {
      if (this.yearSelected === null) {
        this.yearValueCtrl.patchValue(this.currentYear);
      } else {
        this.yearValueCtrl.patchValue(this.yearSelected);
      }
      this.statusBtnNext = false;
      this.statusBtnPrev = false;
      this.togglePicker = true;
    }
  }

  monthSelectAction(event: any, selectedMonth: { month: string, numberMonth: number }): void {
    if (event && event.type === 'click') {
      const monthAndYearValue = `${selectedMonth.month}, ${this.yearValueCtrl.value}`;
      this.monthAndYearValueCtrl.patchValue(monthAndYearValue);
      this.onChange(`${this.yearValueCtrl.value}-${selectedMonth.numberMonth}-01`);
      this.yearSelected = this.yearValueCtrl.value;
      this.monthSelected = selectedMonth.numberMonth;
      this.togglePicker = false;
    }
  }

  selectedMonth(item: any): boolean {
    let isSelected = false;

    if (this.monthSelected && (item.numberMonth === Number(this.monthSelected))) {
      isSelected = true;
    }
    return isSelected;
  }

  closePicker(event: any): void {
    if (event && event.type === 'mouseleave') {
      this.togglePicker = false;
    }
  }

  prev(): void {
    if (!this.statusBtnPrev) {
      const prevItem = (Number(this.yearValueCtrl.value) - 1);

      if (this.getYears().includes(prevItem)) {
        this.enableButtons();
        this.yearValueCtrl.patchValue(prevItem);
      } else {
        this.statusBtnPrev = true;
        this.statusBtnNext = false;
      }
    }
  }

  next(): void {
    if (!this.statusBtnNext) {
      const nextItem = (Number(this.yearValueCtrl.value) + 1);

      if (this.getYears().includes(nextItem)) {
        this.enableButtons();
        this.yearValueCtrl.patchValue(nextItem);
      } else {
        this.statusBtnNext = true;
        this.statusBtnPrev = false;
      }
    }
  }

  changeItems(): void {
    this.enableButtons();
  }

  enableButtons(): void {
    this.statusBtnNext = false;
    this.statusBtnPrev = false;
  }

  isValidCtrl(): boolean {
    return (this.statusControl === null);
  }

  ngOnDestroy(): void {}
}



// WEBPACK FOOTER //
// ./src/app/utils/month-picker/month-picker.component.ts